import { create } from 'zustand';
import { OrganizationType } from '../types/organization.type';
import type {
  EsgDashboardByBranchResponse,
  EsgDashboardMonthlyResponse,
  EsgDashboardOverviewResponse,
} from '../types/esg-dashboard.type';
import {
  getEsgDashboardByBranch,
  getEsgDashboardMonthly,
  getEsgDashboardOverview,
  getEsgPersonaSegment,
} from '../services/esg-dashboard.service';

interface EsgDashboardState {
  monthly: EsgDashboardMonthlyResponse | null;
  overview: EsgDashboardOverviewResponse | null;
  byBranch: EsgDashboardByBranchResponse | null;
  isLoading: boolean;
  error: string | null;
  fetchEsgDashboard: (
    orgId: string,
    orgType: OrganizationType,
    monthDateIso: string
  ) => Promise<void>;
  reset: () => void;
}

export const useEsgDashboardStore = create<EsgDashboardState>((set) => ({
  monthly: null,
  overview: null,
  byBranch: null,
  isLoading: false,
  error: null,

  reset: () =>
    set({
      monthly: null,
      overview: null,
      byBranch: null,
      isLoading: false,
      error: null,
    }),

  fetchEsgDashboard: async (orgId, orgType, monthDateIso) => {
    const persona = getEsgPersonaSegment(orgType);
    const monthQuery = { month_date: monthDateIso };

    set({ isLoading: true, error: null });

    try {
      const [monthly, overview, byBranch] = await Promise.all([
        getEsgDashboardMonthly(persona, orgId, monthQuery),
        getEsgDashboardOverview(persona, orgId),
        getEsgDashboardByBranch(persona, orgId, monthQuery),
      ]);

      set({
        monthly,
        overview,
        byBranch,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({
        monthly: null,
        overview: null,
        byBranch: null,
        isLoading: false,
        error:
          err instanceof Error ? err.message : 'Failed to load ESG dashboard data',
      });
    }
  },
}));
